import type { ReactNode } from "react";

// Minimal, safe markdown for MoneyBot answers: headings, bullet/numbered lists, **bold**,
// *italic*, `code`. Builds React nodes (no innerHTML), so model output can't inject markup.

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*\s][^*]*\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const k = `${keyBase}-${i++}`;
    if (tok.startsWith("**") || tok.startsWith("__")) out.push(<strong key={k} style={{ fontWeight: 900 }}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) out.push(<span key={k} style={{ fontWeight: 900 }}>{tok.slice(1, -1)}</span>);
    else out.push(<em key={k}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function ChatMarkdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  text.split("\n").forEach((raw, i) => {
    const line = raw.trimEnd();
    const k = `l${i}`;
    if (!line.trim()) {
      blocks.push(<div key={k} style={{ height: 8 }} />);
      return;
    }
    const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (heading) {
      blocks.push(<div key={k} style={{ fontWeight: 900, fontSize: 15, marginTop: 2 }}>{inline(heading[1], k)}</div>);
      return;
    }
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) return; // horizontal rule — just drop it
    const list = line.match(/^(\s*)([-*•]|\d+[.)])\s+(.*)$/);
    if (list) {
      const indent = Math.min(list[1].length, 6) * 3;
      const marker = /\d/.test(list[2]) ? list[2] : "•";
      blocks.push(
        <div key={k} style={{ display: "flex", gap: 6, paddingLeft: 2 + indent }}>
          <span style={{ flexShrink: 0, fontWeight: 900 }}>{marker}</span>
          <span style={{ minWidth: 0 }}>{inline(list[3], k)}</span>
        </div>
      );
      return;
    }
    blocks.push(<div key={k}>{inline(line, k)}</div>);
  });
  return <div style={{ wordBreak: "break-word" }}>{blocks}</div>;
}
